import React from 'react';
import { motion } from 'framer-motion';
import Button from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';

const allServices = [
  {
    title: "Emergency Plumbing",
    desc: "When disaster strikes, you need immediate help. We handle burst pipes, severe leaks, overflowing toilets, and total loss of water pressure 24/7.",
    benefits: ["Arrive within 60 mins", "Stop damage instantly", "Fully equipped trucks"],
    icon: "AlertOctagon"
  },
  {
    title: "Drain Cleaning & Unclogging",
    desc: "Stubborn clogs require professional tools. We use motorized augers and hydro-jetting to clear main sewer lines, sinks, showers, and floor drains.",
    benefits: ["Video camera inspection", "Hydro-jetting available", "Prevent future backups"],
    icon: "Activity"
  },
  {
    title: "Water Heater Services",
    desc: "No hot water? We repair all makes and models, and install high-efficiency traditional tank and tankless water heaters.",
    benefits: ["Same-day replacement", "Energy efficient options", "Haul away old unit"],
    icon: "Thermometer"
  },
  {
    title: "Leak Detection & Repair",
    desc: "Hidden leaks can cause thousands in structural damage. We use acoustic and thermal imaging to find leaks behind walls and under slabs without destruction.",
    benefits: ["Non-invasive tech", "Save on water bills", "Prevent mold growth"],
    icon: "Search"
  },
  {
    title: "Toilet Repair & Installation",
    desc: "Running, leaking, or rocking toilets waste water and damage floors. We fix flappers, fill valves, wax rings, or install modern low-flow models.",
    benefits: ["Water conservation", "Fix phantom flushes", "Premium brands"],
    icon: "Droplet"
  },
  {
    title: "Sump Pumps & Backwater Valves",
    desc: "Protect your Montreal basement from flooding and municipal sewer backups with professional sump pump and backwater valve installations.",
    benefits: ["City rebate assistance", "Battery backup options", "Peace of mind"],
    icon: "Shield"
  }
];

export default function Services() {
  return (
    <div className="pt-8 pb-20">
      <div className="bg-primary text-white py-16 mb-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Our Plumbing Services</h1>
          <p className="text-xl max-w-2xl mx-auto text-blue-100">Comprehensive residential and commercial plumbing solutions tailored for Montreal properties.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {allServices.map((service, idx) => (
            <motion.div 
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm hover:shadow-lg transition-shadow"
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-blue-50 text-primary rounded-lg flex items-center justify-center shrink-0">
                  <SafeIcon name={service.icon} className="text-2xl" />
                </div>
                <h2 className="text-2xl font-bold text-slate-800 mt-2">{service.title}</h2>
              </div>
              
              <p className="text-slate-600 mb-6 leading-relaxed">{service.desc}</p>
              
              <div className="mb-8">
                <h4 className="font-semibold text-slate-800 mb-3">Service Highlights:</h4>
                <ul className="space-y-2">
                  {service.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-center gap-2 text-slate-600">
                      <SafeIcon name="Check" className="text-green-500" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>

              <Button to="/request-service" variant="outline" className="w-full justify-center">
                Request this Service
              </Button>
            </motion.div>
          ))}
        </div>

        <div className="mt-20 bg-slate-50 rounded-3xl p-8 md:p-12 text-center border border-slate-200">
          <h2 className="text-3xl font-bold mb-4">Don't see what you need?</h2>
          <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">We are full-service master plumbers. If water runs through it, we can fix it, replace it, or install it.</p>
          <Button to="/contact" variant="primary" className="px-8 py-4 text-lg">Contact Us To Discuss Your Project</Button>
        </div>
      </div>
    </div>
  );
}